import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Quote, Award, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { STORIES } from "../data/content";

const initials = (name) => {
  const p = name.replace(/[^A-Za-z ]/g, "").trim().split(/\s+/);
  return (p[0]?.[0] || "I") + (p[1]?.[0] || "S");
};

export const StorySpotlight = ({ onGive }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % STORIES.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const story = STORIES[i];
  const go = (dir) => setI((v) => (v + dir + STORIES.length) % STORIES.length);

  return (
    <section className="py-14 md:py-16 bg-offwhite" data-testid="story-spotlight" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="container-x max-w-4xl">
        <div className="i-card overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-12">
            <div className="md:col-span-4 bg-navy text-offwhite p-8 flex flex-col justify-between">
              <div>
                <div className="label-caps text-gold flex items-center gap-2"><Heart className="h-3.5 w-3.5" /> Why it matters</div>
                <p className="mt-4 text-sm text-offwhite/75 leading-relaxed">Real reset stories from veterans your gift helps house and stabilize.</p>
              </div>
              <button onClick={onGive} data-testid="story-give-btn" className="mt-6 inline-flex items-center justify-center gap-2 bg-gold text-charcoal px-5 py-3 rounded-lg text-sm font-bold uppercase tracking-wide hover:bg-gold-deep transition-colors">
                Fund a reset
              </button>
            </div>

            <div className="md:col-span-8 p-8 md:p-10 relative min-h-[280px] flex flex-col justify-center">
              <Quote className="h-8 w-8 text-gold/50" strokeWidth={1.5} />
              <AnimatePresence mode="wait">
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="story-content"
                >
                  <p className="mt-4 text-lg md:text-xl text-charcoal font-medium leading-relaxed">"{story.quote}"</p>
                  <div className="mt-6 flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-navy text-gold flex items-center justify-center font-heading text-sm font-black shrink-0">{initials(story.name)}</div>
                    <div>
                      <div className="font-heading text-base font-bold text-navy">{story.name}</div>
                      <div className="text-sm text-slate">{story.branch}</div>
                    </div>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal px-3.5 py-1.5 text-xs font-bold">
                    <Award className="h-3.5 w-3.5" /> {story.outcome}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center gap-3">
                <button onClick={() => go(-1)} aria-label="Previous story" data-testid="story-prev" className="h-9 w-9 rounded-full border border-softgray flex items-center justify-center text-navy hover:border-navy transition-colors"><ChevronLeft className="h-4 w-4" /></button>
                <button onClick={() => go(1)} aria-label="Next story" data-testid="story-next" className="h-9 w-9 rounded-full border border-softgray flex items-center justify-center text-navy hover:border-navy transition-colors"><ChevronRight className="h-4 w-4" /></button>
                <div className="flex items-center gap-1.5 ml-2">
                  {STORIES.map((_, idx) => (
                    <button key={idx} onClick={() => setI(idx)} aria-label={`Story ${idx + 1}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-gold" : "w-2 bg-softgray"}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-charcoal/55">Representative stories. Names and details shown with permission or anonymized.</p>
      </div>
    </section>
  );
};

export default StorySpotlight;
